import { MarketSelectableCard, MarketStatusPill } from '~/features/shared/market-content';
import { JobOpportunity, OpportunityStatus } from '../types';
import { formatHourlyRate, formatRelativeTime } from '../utils';

type Props = {
  opportunity: JobOpportunity;
  isSelected: boolean;
  onSelect: (responseId: number) => void;
};

type StatusDisplay = {
  label: string;
  status: Parameters<typeof MarketStatusPill>[0]['status'];
  pulse?: boolean;
};

const getStatusDisplay = (
  status: OpportunityStatus,
  turn: JobOpportunity['turn']
): StatusDisplay => {
  if (
    (status === 'MESSAGED' || status === 'NEGOTIATING' || status === 'PENDING_ACCEPTANCE') &&
    turn === 'YOUR_TURN'
  ) {
    return { label: 'Awaiting You', status: 'yourTurn', pulse: true };
  }
  if (status === 'NEGOTIATING') {
    return { label: 'Negotiating', status: 'pending' };
  }

  const map: Record<string, StatusDisplay> = {
    MESSAGED: { label: 'Messaged', status: 'responded' },
    APPLIED: { label: 'Applied', status: 'neutral' },
    PENDING_ACCEPTANCE: { label: 'Pending Acceptance', status: 'pending' },
    ACCEPTED: { label: 'Accepted', status: 'accepted' },
    REJECTED: { label: 'Declined', status: 'danger' },
    WITHDRAWN: { label: 'Withdrawn', status: 'inactive' },
  };

  return map[status] ?? { label: status, status: 'neutral' };
};

export const OpportunityCard = ({ opportunity, isSelected, onSelect }: Props) => {
  const { listing } = opportunity;
  const statusDisplay = getStatusDisplay(opportunity.status, opportunity.turn);

  return (
    <MarketSelectableCard
      selected={isSelected}
      className="mb-3 shrink-0"
      onClick={() => onSelect(opportunity.responseId)}
      dataCy={`opportunity-card-${opportunity.responseId}`}
    >
      <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2">
        <MarketStatusPill status="responded" label="JOB" />
        <MarketStatusPill status="neutral" label={listing.jobType} />
        <span className="shrink-0 text-xs text-content-secondary tabular-nums @max-[280px]/sidebar:hidden">
          #{listing.id}
        </span>
      </div>

      <p
        className="body-text-sb mb-1 line-clamp-2 break-words leading-snug text-content-primary"
        data-cy="opportunity-title"
        title={listing.jobTitle}
      >
        {listing.jobTitle}
      </p>

      <div
        className="flex min-w-0 max-w-full flex-wrap items-center gap-1 overflow-hidden text-xs text-content-secondary"
        data-cy="opportunity-metadata"
      >
        {listing.specialtyName && (
          <span className="min-w-0 truncate" title={listing.specialtyName}>
            {listing.specialtyName}
          </span>
        )}
        {listing.stateCodes.length > 0 && (
          <>
            <span className="shrink-0">&middot;</span>
            <span className="shrink-0">
              {listing.stateCodes.length > 3
                ? `${listing.stateCodes.length} states`
                : listing.stateCodes.join(', ')}
            </span>
          </>
        )}
        {listing.pricePerHour && (
          <>
            <span className="shrink-0">&middot;</span>
            <span className="shrink-0">{formatHourlyRate(listing.pricePerHour)}</span>
          </>
        )}
      </div>

      <div className="mt-3 flex min-w-0 items-center justify-between gap-2 border-t border-grey-900 pt-2">
        <MarketStatusPill
          status={statusDisplay.status}
          label={statusDisplay.label}
          pulse={statusDisplay.pulse}
        />
        <span
          className="shrink-0 text-xs text-content-secondary @max-[280px]/sidebar:hidden"
          data-cy="opportunity-updated-at"
        >
          {formatRelativeTime(opportunity.updatedAt)}
        </span>
      </div>
    </MarketSelectableCard>
  );
};
